import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type {
  OpenVisitResponse,
  OperationType,
  PaginatedResponse,
  PortalForm,
  ServiceLink,
  SubmitVisitResponse,
  TariffAgreement,
} from '@core/types/api';

/**
 * The trays of the counter. `all` is the whole cartera; the other three are what the
 * server calls `billability`: what can be billed now, what nobody has mapped, and
 * what cannot be billed to this insurer (decided so, or its act left the tariff).
 */
export type CounterTray = 'all' | 'billable' | 'pending' | 'blocked';

export const COUNTER_TRAYS: readonly CounterTray[] = ['all', 'billable', 'pending', 'blocked'];

export interface ListCounterServicesArgs {
  insurer: string;
  tray: CounterTray;
  page: number;
  pageSize: number;
  ordering?: string;
  search?: string;
  agreement?: string;
}

export interface CounterPage {
  results: ServiceLink[];
  count: number;
}

export interface OpenVisitArgs {
  insurer: string;
  /** The raw card read, passed through to the portal and never stored. */
  card_read: string;
}

export interface SubmitVisitArgs {
  /** The uuid of the open-visit job. */
  visit: string;
  /** A `value` of the professionals the open visit offered. */
  professional: string;
  /** The ticked service links. */
  links: string[];
}

export interface CountCounterServicesArgs {
  insurer: string;
  tray: Exclude<CounterTray, 'all'>;
}

function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  return (response as PaginatedResponse<T> | null)?.results ?? [];
}

function counterParams({ insurer, tray, agreement }: Pick<ListCounterServicesArgs, 'insurer' | 'tray' | 'agreement'>) {
  return {
    insurer,
    in_cartera: 'true',
    ...(tray === 'all' ? {} : { billability: tray }),
    ...(agreement ? { agreement } : {}),
  };
}

/**
 * Billing API: what the counter needs to bill a visit to one insurer.
 *
 * The same endpoints the tariffs section reads, with its own cache: a feature
 * never imports another's api. The counter reads the whole cartera of the company
 * against the patient's insurer (`in_cartera=true`, every status), priced at the
 * agreement on the card, so a service nobody has mapped is seen and refused
 * rather than silently missing. COMPANY-scoped, like the links it reads
 * (`TENANT_SCOPED_APIS` and `COMPANY_SCOPED_APIS`).
 */
export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery,
  endpoints: (builder) => ({
    listAgreements: builder.query<TariffAgreement[], string>({
      query: (insurer) => ({ url: '/api/tariffs/agreements/', method: 'GET', params: { insurer } }),
      transformResponse: (response: unknown) => unwrapList<TariffAgreement>(response),
    }),

    listOperationTypes: builder.query<OperationType[], string>({
      query: (insurer) => ({ url: '/api/tariffs/operation-types/', method: 'GET', params: { insurer } }),
      transformResponse: (response: unknown) => unwrapList<OperationType>(response),
    }),

    /** One page of the cartera, seen from the insurer, in the order the counter reads it. */
    listCounterServices: builder.query<CounterPage, ListCounterServicesArgs>({
      query: ({ page, pageSize, ordering, search, ...scope }) => ({
        url: '/api/tariffs/links/',
        method: 'GET',
        params: {
          ...counterParams(scope),
          page,
          page_size: pageSize,
          ordering: ordering || 'his_service__name',
          ...(search ? { search } : {}),
        },
      }),
      transformResponse: (response: PaginatedResponse<ServiceLink> | null) => ({
        results: response?.results ?? [],
        count: response?.count ?? 0,
      }),
    }),

    /** The aggregator's cascade for the insurer: the doctors the counter picks from (ADR-0020). */
    getPortalForm: builder.query<PortalForm, string>({
      query: (insurer) => ({ url: '/api/tariffs/portal-form/', method: 'GET', params: { insurer } }),
    }),

    /** Ask the portal for the cascade again; the job runs in the background. */
    refreshPortalForm: builder.mutation<{ job: string }, string>({
      query: (insurer) => ({ url: '/api/tariffs/portal-form/refresh/', method: 'POST', body: { insurer } }),
    }),

    /**
     * Open the visit on the portal with the card read (Redsa). The job runs before the
     * call answers: its status and the doctors who may sign the visit come back.
     */
    openVisit: builder.mutation<OpenVisitResponse, OpenVisitArgs>({
      query: (body) => ({ url: '/api/portals/jobs/open-visit/', method: 'POST', body }),
    }),

    /** Send the open visit to the portal with the ticked services, signed by the doctor. */
    submitVisit: builder.mutation<SubmitVisitResponse, SubmitVisitArgs>({
      query: (body) => ({ url: '/api/portals/jobs/submit-visit/', method: 'POST', body }),
    }),

    /** How many services one tray holds, for the tray's own count. One row is enough: we read the total. */
    countCounterServices: builder.query<number, CountCounterServicesArgs>({
      query: (scope) => ({
        url: '/api/tariffs/links/',
        method: 'GET',
        params: { ...counterParams(scope), page: 1, page_size: 1 },
      }),
      transformResponse: (response: PaginatedResponse<ServiceLink> | null) => response?.count ?? 0,
    }),
  }),
});

export const {
  useListAgreementsQuery,
  useListOperationTypesQuery,
  useListCounterServicesQuery,
  useCountCounterServicesQuery,
  useGetPortalFormQuery,
  useRefreshPortalFormMutation,
  useOpenVisitMutation,
  useSubmitVisitMutation,
} = billingApi;
